import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import axiosInstance from "config/axiosInstance";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { formatDateTimePeru } from "utils/datePeru";
import { formatDisplayPhone } from "./NotificationRecipientsSummary";
import RecentSendsTable from "./RecentSendsTable";
import { formatSessionLabel } from "utils/whatsappUi";

function formatDuration(seconds) {
  if (seconds == null || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m} min`;
  return `${seconds}s`;
}

function MetricCard({ title, value, subtitle, accent = "#0075FF", loading = false }) {
  return (
    <Card sx={{ p: 2.5, height: "100%" }}>
      <VuiTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
        {title}
      </VuiTypography>
      {loading ? (
        <Skeleton width="40%" height={40} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
      ) : (
        <VuiTypography variant="h4" color="white" fontWeight="bold" mb={0.5}>
          {value}
        </VuiTypography>
      )}
      {subtitle && (
        <VuiTypography variant="caption" sx={{ color: accent }}>
          {subtitle}
        </VuiTypography>
      )}
    </Card>
  );
}

function mergeSessionForDisplay(metricsSession, liveSession) {
  const fromMetrics = metricsSession && typeof metricsSession === "object" ? metricsSession : {};
  const fromLive = liveSession && typeof liveSession === "object" ? liveSession : {};

  if (fromLive.connected || fromLive.status === "connected") {
    return {
      ...fromMetrics,
      ...fromLive,
      connected: true,
      status: "connected",
    };
  }

  if (fromMetrics.connected || fromMetrics.status) {
    return fromMetrics;
  }

  if (fromLive.status || fromLive.connectedPhone) {
    return { ...fromMetrics, ...fromLive };
  }

  return fromMetrics;
}

export default function MetricsPanel({ refreshKey = 0, liveSession = null }) {
  const [metrics, setMetrics] = useState(null);
  const [fallbackSession, setFallbackSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      let metricsSession = null;

      try {
        const res = await axiosInstance.get("/whatsapp/metrics");
        if (active && res.data?.success !== false) {
          setMetrics(res.data.metrics);
          metricsSession = res.data.metrics?.session || null;
        }
      } catch {
        if (active) setMetrics(null);
      }

      try {
        const statusRes = await axiosInstance.get("/whatsapp/session/status");
        if (active && statusRes.data?.success !== false) {
          const body = statusRes.data;
          const metricsConnected =
            metricsSession?.connected || metricsSession?.status === "connected";
          const liveConnected = body.connected || body.status === "connected";
          if (liveConnected || !metricsConnected) {
            setFallbackSession(body);
          } else if (!body.status && !body.connectedPhone) {
            setFallbackSession(null);
          } else {
            setFallbackSession(body);
          }
        }
      } catch {
        if (active && !metricsSession) {
          setFallbackSession(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [refreshKey]);

  const queue = metrics?.queue || {};
  const today = metrics?.today || {};
  const totals = metrics?.totals || {};
  const session = mergeSessionForDisplay(metrics?.session, liveSession || fallbackSession);
  const audit = metrics?.sessionAudit || {};

  const sessionAccent =
    session.connected || session.status === "connected"
      ? "#01B574"
      : session.status === "paused"
        ? "#FFB547"
        : "#A0AEC0";

  return (
    <VuiBox>
      <VuiTypography variant="h5" color="white" fontWeight="bold" mb={0.5}>
        Cola y métricas
      </VuiTypography>
      <VuiTypography variant="button" color="text" fontWeight="regular" mb={2} display="block">
        Sistema de alertas WhatsApp
      </VuiTypography>

      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Enviados hoy"
            value={today.enviados ?? 0}
            accent="#01B574"
            loading={loading && !metrics}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Fallidos hoy"
            value={today.fallidos ?? today.errores ?? 0}
            accent="#E31A1A"
            loading={loading && !metrics}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Pendientes"
            value={queue.pendiente ?? 0}
            accent="#FFB547"
            loading={loading && !metrics}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Reintentos"
            value={queue.retries ?? 0}
            accent="#FFB547"
            loading={loading && !metrics}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Sesión"
            value={formatSessionLabel(session, audit)}
            accent={sessionAccent}
            loading={loading && !metrics}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Número conectado"
            value={
              session.connectedPhone
                ? formatDisplayPhone(session.connectedPhone)
                : "—"
            }
            accent="#01B574"
            loading={loading && !metrics}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Tiempo online"
            value={
              audit.uptimeSeconds != null ? formatDuration(audit.uptimeSeconds) : "—"
            }
            subtitle={
              session.connected && audit.connected_at
                ? `Desde ${formatDateTimePeru(audit.connected_at)}`
                : "Sin conexión activa"
            }
            accent="#0075FF"
            loading={loading && !metrics}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Última desconexión"
            value={
              audit.disconnected_at
                ? formatDateTimePeru(audit.disconnected_at)
                : "—"
            }
            accent="#A0AEC0"
            loading={loading && !metrics}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Total enviados"
            value={totals.enviados ?? queue.enviado ?? 0}
            accent="#01B574"
            loading={loading && !metrics}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Total fallidos"
            value={totals.fallidos ?? queue.error ?? 0}
            accent="#E31A1A"
            loading={loading && !metrics}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            title="Enviando ahora"
            value={queue.procesando ?? 0}
            accent="#0075FF"
            loading={loading && !metrics}
          />
        </Grid>
      </Grid>

      <RecentSendsTable refreshKey={refreshKey} limit={12} embedded={false} />
    </VuiBox>
  );
}

export { formatDuration };
